import { useState, useCallback } from 'react';
import { useApi, fetchApiRef } from '@backstage/frontend-plugin-api';
import type {
  CheckDefinition,
  CheckResult,
} from '@internal/backstage-plugin-pulse-common';

const errorFromResponse = async (
  response: Response,
  fallback: string,
): Promise<Error> => {
  try {
    const data = await response.json();
    return new Error(data.error ?? fallback);
  } catch {
    return new Error(fallback);
  }
};

/** Evaluates a (possibly unsaved) check against an entity without persisting. */
export const useDryRunCheck = () => {
  const { fetch } = useApi(fetchApiRef);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const dryRun = useCallback(
    async (
      check: CheckDefinition,
      entityRef: string,
    ): Promise<CheckResult[]> => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('plugin://pulse/checks/dry-run', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ check, entityRef }),
        });
        if (!response.ok) {
          throw await errorFromResponse(
            response,
            `Dry run failed: ${response.status}`,
          );
        }
        const data = await response.json();
        return (data.results ?? []) as CheckResult[];
      } catch (error_) {
        const err = error_ as Error;
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetch],
  );

  return { dryRun, loading, error };
};
